import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import React from 'react'
import { useCalculateButtonStyles } from 'src/publisher/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/publisher/shared/hooks/use-theme-color/use-selected-button-color'
import { Button as CommonButton } from 'src/shared/components/button/button'
import { CommonButtonEntityInterface } from 'src/shared/schemas/buttons/common-button-entity.schema'

export interface BasePublisherButtonProps {
  entityOrSubEntity: Omit<CommonButtonEntityInterface, 'type' | 'parentId'>
  onClick: (e: React.SyntheticEvent) => void
  isLoading?: boolean
  errors?: string[]
  testId: string
  endElement?: React.ReactNode
}

// TODO: reuse it in other button components
export const BasePublisherButton = ({
  entityOrSubEntity,
  isLoading,
  errors,
  onClick,
  testId,
  endElement,
}: BasePublisherButtonProps) => {
  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entityOrSubEntity.border,
    mobileBorder: entityOrSubEntity.mobileBorder,
    padding: entityOrSubEntity.padding,
    mobilePadding: entityOrSubEntity.mobilePadding,
    buttonType: entityOrSubEntity.buttonType,
    fontSize: entityOrSubEntity.textFontSize,
    mobileFontSize: entityOrSubEntity.mobileTextFontSize,
    lineHeight: entityOrSubEntity.lineHeight,
    mobileLineHeight: entityOrSubEntity.mobileLineHeight,
    fontFamily: entityOrSubEntity.textFontFamily,
    mobileFontFamily: entityOrSubEntity.mobileTextFontFamily,
    fontWeight: entityOrSubEntity.textFontWeight,
    mobileFontWeight: entityOrSubEntity.mobileTextFontWeight,
    fontStyle: entityOrSubEntity.textFontStyle,
    mobileFontStyle: entityOrSubEntity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(entityOrSubEntity.buttonType)

  return (
    <StyleWrapper
      flex
      flexColumn
      margin={entityOrSubEntity?.margin}
      mobileMargin={entityOrSubEntity?.mobileMargin}
      appearance={entityOrSubEntity.appearance}
    >
      <CommonButton
        dataTestId={testId}
        iconClassNameBefore={entityOrSubEntity.iconClassNameBefore}
        iconClassNameAfter={entityOrSubEntity.iconClassNameAfter}
        onClick={onClick}
        disabled={isLoading}
        text={entityOrSubEntity.text}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        subText={entityOrSubEntity.subText}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        alignSelf={entityOrSubEntity.alignSelf}
        mobileAlignSelf={entityOrSubEntity.mobileAlignSelf}
        background={
          (entityOrSubEntity.background ?? entityOrSubEntity.backgroundColor) ||
          buttonBackgroundColor
        }
        mobileBackground={
          (entityOrSubEntity.mobileBackground ??
            entityOrSubEntity.mobileBackgroundColor) ||
          buttonBackgroundColor
        }
        attrId={entityOrSubEntity.htmlAttrId}
        width={entityOrSubEntity.width}
        mobileWidth={entityOrSubEntity.mobileWidth}
        textColor={entityOrSubEntity.textColor || buttonTextColor}
        subTextColor={entityOrSubEntity.subTextColor}
        mobileTextColor={entityOrSubEntity.mobileTextColor || buttonTextColor}
        mobileSubTextColor={entityOrSubEntity.mobileSubTextColor}
        textFontSize={calculatedFontSize.desktop}
        mobileTextFontSize={calculatedFontSize.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        subTextFontSize={entityOrSubEntity.subTextFontSize}
        mobileSubTextFontSize={entityOrSubEntity.mobileSubTextFontSize}
        textFontFamily={calculatedFontFamily.desktop}
        textFontWeight={calculatedFontWeight.desktop}
        textFontStyle={calculatedFontStyle.desktop}
        subTextFontFamily={entityOrSubEntity.subTextFontFamily}
        subTextFontWeight={entityOrSubEntity.subTextFontWeight}
        subTextFontStyle={entityOrSubEntity.subTextFontStyle}
        mobileTextFontFamily={calculatedFontFamily.mobile}
        mobileTextFontWeight={calculatedFontWeight.mobile}
        mobileTextFontStyle={calculatedFontStyle.mobile}
        mobileSubTextFontFamily={entityOrSubEntity.mobileSubTextFontFamily}
        mobileSubTextFontWeight={entityOrSubEntity.mobileSubTextFontWeight}
        mobileSubTextFontStyle={entityOrSubEntity.mobileSubTextFontStyle}
        boxShadow={entityOrSubEntity.boxShadow}
        mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
        hover={{
          ...entityOrSubEntity.hover,
          backgroundColor:
            entityOrSubEntity?.hover?.backgroundColor ||
            hoveredButtonBackgroundColor,
        }}
        mobileHover={{
          ...entityOrSubEntity.mobileHover,
          backgroundColor:
            entityOrSubEntity?.mobileHover?.backgroundColor ||
            entityOrSubEntity?.hover?.backgroundColor ||
            hoveredButtonBackgroundColor,
        }}
      />
      <FieldErrors errors={errors} align="center" />
      {endElement}
    </StyleWrapper>
  )
}
